// New 3-tier engagement system types

export type TierType = 'awareness' | 'presence' | 'continuity' | 'beyond';

export type BadgeType = 
  | 'first_words'
  | 'presence'
  | 'honesty'
  | 'connection'
  | 'care'
  | 'continuity'
  | 'legacy_builder';

export interface Badge {
  id: string;
  badge_key: BadgeType;
  name: string;
  description: string;
  icon: string;
  requirement_description: string;
  points: number;
  created_at: string;
}

export interface UserBadge {
  id: string;
  user_id: string;
  badge_id: string;
  earned_at: string;
  badge?: Badge;
}

export interface UserEngagement {
  id: string;
  user_id: string;
  current_level: string; // deprecated, kept for compatibility
  current_tier: TierType;
  total_points: number;
  badges_earned: number;
  tier_actions_count: number;
  pending_reward_credits: number;
  pending_gift_discount: boolean;
  legacy_builder_unlocked: boolean;
  last_activity_at: string;
  updated_at: string;
}

export interface PendingReward {
  id: string;
  user_id: string;
  reward_type: 'credits' | 'discount' | 'legacy_status';
  credits_amount: number;
  discount_percent: number;
  tier_completed: TierType | null;
  claimed_at: string | null;
  created_at: string;
}

// Tier configuration
export const TIER_CONFIG: Record<TierType, {
  name: string;
  displayName: string;
  minPoints: number;
  maxPoints: number;
  actionsRequired: number;
  color: string;
  reward: {
    credits?: number;
    discount?: number;
    legacyBuilder?: boolean;
  };
}> = {
  awareness: {
    name: 'awareness',
    displayName: 'Awareness',
    minPoints: 0,
    maxPoints: 100,
    actionsRequired: 7,
    color: 'hsl(var(--primary))',
    reward: { credits: 3 },
  },
  presence: {
    name: 'presence',
    displayName: 'Presence',
    minPoints: 101,
    maxPoints: 200,
    actionsRequired: 7,
    color: 'hsl(var(--secondary))',
    reward: { discount: 15 },
  },
  continuity: {
    name: 'continuity',
    displayName: 'Continuity',
    minPoints: 201,
    maxPoints: 300,
    actionsRequired: 7,
    color: 'hsl(var(--accent))',
    reward: { credits: 3, discount: 20, legacyBuilder: true },
  },
  beyond: {
    name: 'beyond',
    displayName: 'Beyond',
    minPoints: 301,
    maxPoints: Infinity,
    actionsRequired: 0, // continuous
    color: 'hsl(var(--primary))',
    reward: { credits: 1 }, // +1 every 100 points
  },
};

export const TIER_ORDER: TierType[] = ['awareness', 'presence', 'continuity', 'beyond'];

// Point values for meaningful actions
export const POINT_VALUES = {
  publish_announcement: 20,
  schedule_announcement: 15,
  treslog_entry: 15,
  return_after_absence: 10,
  ai_assist_meaningful: 10,
  mark_as_legacy: 15,
  complete_template_fully: 15,
};

// Tier transition copy - supportive, non-gamified language (keyed by language)
export const TIER_TRANSITION_COPY: Record<TierType, Record<string, { title: string; message: string }>> = {
  awareness: {
    en: { title: 'Your journey has begun', message: 'You\'ve taken the first step toward expressing what matters.' },
    es: { title: 'Tu viaje ha comenzado', message: 'Has dado el primer paso para expresar lo que importa.' },
    fr: { title: 'Votre voyage a commencé', message: 'Vous avez fait le premier pas vers l\'expression de ce qui compte.' },
    pt: { title: 'Sua jornada começou', message: 'Você deu o primeiro passo para expressar o que importa.' },
    ar: { title: 'بدأت رحلتك', message: 'لقد اتخذت الخطوة الأولى نحو التعبير عما يهم.' },
    ja: { title: '旅が始まりました', message: '大切なことを表現する第一歩を踏み出しました。' },
    ko: { title: '여정이 시작되었습니다', message: '중요한 것을 표현하기 위한 첫 걸음을 내디뎠습니다.' },
    id: { title: 'Perjalananmu telah dimulai', message: 'Kamu telah mengambil langkah pertama untuk mengekspresikan apa yang penting.' },
  },
  presence: {
    en: { title: 'You are present', message: 'Your consistent care is making a difference in the lives you touch.' },
    es: { title: 'Estás presente', message: 'Tu cuidado constante está haciendo la diferencia en las vidas que tocas.' },
    fr: { title: 'Vous êtes présent', message: 'Votre attention constante fait la différence dans les vies que vous touchez.' },
    pt: { title: 'Você está presente', message: 'Seu cuidado constante está fazendo a diferença nas vidas que você toca.' },
    ar: { title: 'أنت حاضر', message: 'اهتمامك المستمر يحدث فرقاً في الحياة التي تلمسها.' },
    ja: { title: 'あなたは存在しています', message: '一貫した思いやりが、あなたが触れる人生に変化をもたらしています。' },
    ko: { title: '당신은 존재하고 있습니다', message: '꾸준한 관심이 당신이 닿는 삶에 변화를 만들고 있습니다.' },
    id: { title: 'Kamu hadir', message: 'Perhatianmu yang konsisten membuat perbedaan dalam kehidupan yang kamu sentuh.' },
  },
  continuity: {
    en: { title: 'You are building something lasting', message: 'Your dedication to meaningful connection has become a practice.' },
    es: { title: 'Estás construyendo algo duradero', message: 'Tu dedicación a la conexión significativa se ha convertido en una práctica.' },
    fr: { title: 'Vous construisez quelque chose de durable', message: 'Votre dévouement à la connexion significative est devenu une pratique.' },
    pt: { title: 'Você está construindo algo duradouro', message: 'Sua dedicação à conexão significativa se tornou uma prática.' },
    ar: { title: 'أنت تبني شيئاً دائماً', message: 'إخلاصك للتواصل ذي المعنى أصبح ممارسة.' },
    ja: { title: '永続的なものを築いています', message: '意味ある繋がりへの献身が習慣になりました。' },
    ko: { title: '지속되는 무언가를 만들고 있습니다', message: '의미 있는 연결에 대한 헌신이 습관이 되었습니다.' },
    id: { title: 'Kamu sedang membangun sesuatu yang abadi', message: 'Dedikasimu pada koneksi bermakna telah menjadi kebiasaan.' },
  },
  beyond: {
    en: { title: 'You continue to grow', message: 'Every moment of expression adds to the legacy you\'re creating.' },
    es: { title: 'Sigues creciendo', message: 'Cada momento de expresión se suma al legado que estás creando.' },
    fr: { title: 'Vous continuez à grandir', message: 'Chaque moment d\'expression s\'ajoute à l\'héritage que vous créez.' },
    pt: { title: 'Você continua a crescer', message: 'Cada momento de expressão contribui para o legado que você está criando.' },
    ar: { title: 'تستمر في النمو', message: 'كل لحظة تعبير تضاف إلى الإرث الذي تبنيه.' },
    ja: { title: 'あなたは成長し続けています', message: '表現の一瞬一瞬が、あなたが築くレガシーに加わります。' },
    ko: { title: '계속 성장하고 있습니다', message: '표현의 모든 순간이 당신이 만들어가는 유산에 더해집니다.' },
    id: { title: 'Kamu terus tumbuh', message: 'Setiap momen ekspresi menambah warisan yang sedang kamu ciptakan.' },
  },
};
